import { useAuth } from "AuthProvider";
import { RoutePaths } from "enums/RoutePaths";
import { signInWithEmailAndPassword, User } from "firebase/auth";
import { Form, Formik } from "formik";
import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Alert,
  Badge,
  Button,
  Checkbox,
  FormikTextField,
  Icon,
  styles as ui,
} from "stories/Admin";
import * as Yup from "yup";
import { auth } from "../../firebase";
import useFetch from "../../hooks/useFetch";
import {
  completeGoogleRedirect,
  googleErrorMessage,
  GoogleSignInResult,
  signInWithGoogle,
} from "../../utils/googleAuth";
import styles from "./Login.module.scss";

const validationSchema = Yup.object({
  username: Yup.string().required("Skriv inn e-posten din"),
  password: Yup.string().required("Skriv inn passordet ditt"),
});

const Login: React.FC = () => {
  const { execute, error, isLoading } = useFetch<any>();
  const navigate = useNavigate();
  const {
    setOrgNumber,
    setUid,
    setIsSuperAdmin,
    setIsMawaqeetAdmin,
    setSelectedOrgNumber,
  } = useAuth();
  const [generalError, setGeneralError] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);

  const [googleLoading, setGoogleLoading] = useState(false);

  /**
   * Felles for e-post og Google: lagre token, spør backend om admin-tilgang, og send brukeren
   * videre. Brukere uten tilgang får siden «Du har ikke admin-tilgang ennå».
   */
  const finishLogin = async (
    user: User,
    setError: (errorMessage: string) => void,
  ) => {
    const idToken = await user.getIdToken();
    sessionStorage.setItem("token", idToken);

    const data = await execute("/api/v1/auth/verifyToken", "POST", {
      email: user.email,
    });

    if (!data) {
      setError("Kunne ikke kontakte iBønn. Prøv igjen om litt.");
      return;
    }

    if (data.allowedOperations) {
      const { uid, allowedOperations } = data;
      const isSuperAdmin = data.isSuperAdmin === true;

      const isMawaqeetAdmin = data.isMawaqeetAdmin === true;

      setUid(uid);
      setOrgNumber(allowedOperations);
      setIsSuperAdmin(isSuperAdmin);
      setIsMawaqeetAdmin(isMawaqeetAdmin);
      setSelectedOrgNumber(allowedOperations);

      // Save details to session storage
      sessionStorage.setItem("uid", uid);
      sessionStorage.setItem("orgNumber", allowedOperations);
      sessionStorage.setItem("selectedOrgNumber", allowedOperations);
      sessionStorage.setItem("isSuperAdmin", JSON.stringify(isSuperAdmin));
      sessionStorage.setItem(
        "isMawaqeetAdmin",
        JSON.stringify(isMawaqeetAdmin),
      );

      // Redirect based on super admin status and selected mosque
      if (isSuperAdmin) {
        navigate("/velg-moske");
      } else {
        navigate("/");
      }
    } else {
      // Innlogget, men ikke admin for noen moské ennå.
      navigate(RoutePaths.NoAccess);
    }
  };

  const handleLogin = async (
    values: { username: string; password: string },
    setError: (errorMessage: string) => void,
  ) => {
    try {
      const email = values.username.trim();
      const password = values.password.trim();

      const userCredential = await signInWithEmailAndPassword(
        auth,
        email,
        password,
      );
      await finishLogin(userCredential.user, setError);
    } catch (err: any) {
      const errorCode = err?.code || "unknown";
      const friendlyMessage = getFriendlyErrorMessage(errorCode);
      setError(friendlyMessage);
    }
  };

  /** Ny Google-bruker: opprett iBønn-brukeren i backend som appene gjør (trengs for admin-tilgang). */
  const registerNewUser = async (user: User) => {
    try {
      await execute(
        "/api/v1/users",
        "POST",
        {
          email: user.email,
          username: user.displayName || "notset",
          uid: user.uid,
        },
        undefined,
        { "Api-key": process.env.REACT_APP_API_KEY || "" },
      );
    } catch {
      // Ikke kritisk: backend oppretter brukeren uansett når en admin-søknad godkjennes.
    }
  };

  const finishGoogle = async (result: GoogleSignInResult | null) => {
    if (!result) return;
    const showError = (message: string) => setGeneralError(message);
    if (result.isNewUser) await registerNewUser(result.user);
    await finishLogin(result.user, showError);
  };

  const handleGoogle = async () => {
    setGeneralError(null);
    setGoogleLoading(true);
    try {
      await finishGoogle(await signInWithGoogle());
    } catch (err: any) {
      const message = googleErrorMessage(err?.code);
      if (message) setGeneralError(message);
    } finally {
      setGoogleLoading(false);
    }
  };

  // Tilbake fra Google etter redirect-innlogging (brukes når popup ikke kunne åpnes).
  useEffect(() => {
    let active = true;
    completeGoogleRedirect()
      .then((result) => {
        if (active && result) {
          setGoogleLoading(true);
          return finishGoogle(result).finally(() => setGoogleLoading(false));
        }
      })
      .catch((err: any) => {
        const message = googleErrorMessage(err?.code);
        if (active && message) setGeneralError(message);
      });
    return () => {
      active = false;
    };
  }, []);

  const getFriendlyErrorMessage = (errorCode: string) => {
    switch (errorCode) {
      case "auth/invalid-email":
        return "Skriv inn en gyldig e-postadresse.";
      case "auth/invalid-password":
        return "Feil passord. Prøv igjen.";
      case "auth/invalid-credential":
        return "Feil e-post eller passord. Sjekk og prøv igjen.";
      case "auth/user-not-found":
        return "Finner ingen konto med denne e-postadressen.";
      case "auth/too-many-requests":
        return "For mange mislykkede forsøk. Vent litt og prøv igjen.";
      default:
        return "Noe gikk galt. Prøv igjen.";
    }
  };

  const errorText = generalError
    ? generalError
    : error
      ? error.text || "Noe gikk galt. Prøv igjen."
      : null;

  return (
    <div className={`${ui.root} ${styles.login}`}>
      <section className={styles.hero}>
        <svg viewBox="0 0 200 200" aria-hidden="true" className={styles.star}>
          <g fill="none" stroke="currentColor" strokeWidth="0.8">
            <path d="M100 10 126 74 190 100 126 126 100 190 74 126 10 100 74 74Z" />
            <path d="M100 40 117 83 160 100 117 117 100 160 83 117 40 100 83 83Z" />
            <circle cx="100" cy="100" r="22" />
          </g>
        </svg>
        <span className={styles.brand}>
          <span className={styles.brandMark}>
            <Icon name="moon" size={24} strokeWidth={2.2} />
          </span>
          iBønn Admin
        </span>
        <div className={styles.heroText}>
          <span className={styles.heroTitle}>
            Alt moskeen trenger, samlet på ett sted.
          </span>
          <span className={styles.heroLead}>
            Jamat-tider, push-meldinger, plakater til TV-skjermen og innhold i
            iBønn-appen.
          </span>
          <div className={styles.roles}>
            <Badge className={styles.roleMint}>Moskéadmin</Badge>
            <Badge className={styles.roleGold}>Mawaqeet</Badge>
            <Badge className={styles.roleWhite}>Superadmin</Badge>
          </div>
        </div>
      </section>

      <section className={styles.formSide}>
        <Formik
          initialValues={{ username: "", password: "" }}
          validationSchema={validationSchema}
          onSubmit={(values, { setSubmitting }) => {
            handleLogin(values, (errorMessage: string) => {
              setGeneralError(errorMessage);
              setSubmitting(false);
            });
          }}
        >
          {({ isSubmitting }) => (
            <Form className={styles.form} aria-label="Logg inn" noValidate>
              <div>
                <h1 className={ui.h1}>Velkommen til iBønn Admin</h1>
                <div className={ui.sub}>
                  Logg inn med e-posten du har fått tilgang med.
                </div>
              </div>

              {errorText && <Alert tone="error">{errorText}</Alert>}

              <FormikTextField
                name="username"
                label="E-post"
                required
                type="email"
                autoComplete="username"
                placeholder="navn@moske.no"
                className={generalError ? ui.inpErr : undefined}
              />
              <FormikTextField
                name="password"
                label="Passord"
                required
                type={showPassword ? "text" : "password"}
                autoComplete="current-password"
              />

              <div className={styles.row}>
                <Checkbox
                  label="Vis passord"
                  checked={showPassword}
                  onChange={(e) => setShowPassword(e.target.checked)}
                />
                <span className={styles.muted}>
                  Glemt passord? Kontakt iBønn.
                </span>
              </div>

              <Button
                type="submit"
                size="lg"
                block
                loading={isSubmitting || isLoading}
              >
                {isSubmitting || isLoading ? "Logger inn …" : "Logg inn"}
              </Button>

              <div className={styles.divider}>
                <span>eller</span>
              </div>

              <button
                type="button"
                className={styles.google}
                onClick={handleGoogle}
                disabled={googleLoading || isSubmitting}
                aria-busy={googleLoading || undefined}
              >
                <GoogleLogo />
                {googleLoading ? "Logger inn …" : "Logg inn med Google"}
              </button>
              <span className={styles.muted}>
                Ny her? «Logg inn med Google» lager en bruker for deg, og så kan
                du søke om admin-tilgang til moskeen din.
              </span>

              <span className={styles.request}>
                Ny moské i iBønn?{" "}
                <Link to={RoutePaths.MosqueRequestForm}>Send forespørsel</Link>
              </span>
              <span className={styles.version}>Versjon 1.2</span>
            </Form>
          )}
        </Formik>
      </section>
    </div>
  );
};

/** Googles «G» i fire farger. */
const GoogleLogo: React.FC = () => (
  <svg viewBox="0 0 48 48" width="20" height="20" aria-hidden="true">
    <path
      fill="#EA4335"
      d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"
    />
    <path
      fill="#4285F4"
      d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"
    />
    <path
      fill="#FBBC05"
      d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"
    />
    <path
      fill="#34A853"
      d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"
    />
  </svg>
);

export default Login;
